import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { Country } from '../../../models/index';
import { UserContext } from '../../contexts';
import { CountryEditPage } from './country-edit-page';
import { EntityChangeLog } from '../../reports';

export function CountriesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<Country[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalCountry, setEditModalCountry] = useState<Country>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'countries.edit'));
  }, [user]);

  const loadContries = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`countries`)
      .then((response: any) => {
        setCountries(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadContries();
  }, [loadContries]);

  const onRefreshClick = () => {
    loadContries();
  };

  const onCountryEditSaved = () => {
    closeEditModal();
    loadContries();
  };

  const openEditModal = () => {
    setEditModalCountry(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByCountry = (country: Country) => {
    setEditModalCountry(country);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalCountry(undefined);
  };

  const openDetailModalByCountry = (country: Country) => {
    openEditModalByCountry(country);
  };

  const deleteCountry = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`countries/${id}`)
      .then(() => {
        setLoading(false);
        loadContries();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteCountry = (id: string) => {
    const onOk = () => deleteCountry(id);
    Modal.confirm({
      title: 'Ülke sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Ülke silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const country = countries?.find(m => m.id === id);
      if (country) {
        openEditModalByCountry(country);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteCountry(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, country: Country) => (
        <Button type="link" onClick={() => openDetailModalByCountry(country)}>
          {name}
        </Button>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, country: Country) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(country.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title=""
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
                Yeni ülke
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={countries} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Country'} recordId={changeLogModalOpen} />
      </Modal>

      <Modal title="Ülke" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <CountryEditPage country={editModalCountry} onCancel={closeEditModal} onSaved={onCountryEditSaved} />
      </Modal>
    </>
  );
}
